import React, { useEffect, useMemo, useState } from "react";
import API from "../api/api";
import Select from "react-select";

function getErrorMessage(error, fallbackMessage) {
  return error.response?.data?.message || fallbackMessage;
}

function isoCodeToFlag(isoCode) {
  if (!isoCode || isoCode.length !== 2) {
    return "";
  }

  return isoCode
    .toUpperCase()
    .split("")
    .map((char) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join("");
}

export default function VisaRouteSelectors({
  disabled,
  selectedOriginCountryId,
  selectedDestinationCountryId,
  selectedApplicationCentreId,
  selectedAppointmentCategoryId,
  selectedSubCategoryId,
  onOriginCountryChange,
  onDestinationCountryChange,
  onApplicationCentreChange,
  onAppointmentCategoryChange,
  onSubCategoryChange,
}) {
  const [countries, setCountries] = useState([]);
  const [applicationCentres, setApplicationCentres] = useState([]);
  const [appointmentCategories, setAppointmentCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [centresLoading, setCentresLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [subCategoriesLoading, setSubCategoriesLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [emptyMessage, setEmptyMessage] = useState("");

  const countriesOptions = useMemo(
    () =>
      countries.map((country) => ({
        value: country.id,
        label: `${country.name} (${country.iso_code})`,
        country,
      })),
    [countries]
  );

  const applicationCentreOptions = useMemo(
    () =>
      applicationCentres.map((centre) => ({
        value: centre.id,
        label: centre.centre_name,
        centre,
      })),
    [applicationCentres]
  );

  const appointmentCategoryOptions = useMemo(
    () =>
      appointmentCategories.map((category) => ({
        value: category.id,
        label: category.category_name,
        category,
      })),
    [appointmentCategories]
  );

  const subCategoryOptions = useMemo(
    () =>
      subCategories.map((subCategory) => ({
        value: subCategory.id,
        label: subCategory.sub_category_name,
        subCategory,
      })),
    [subCategories]
  );

  const selectedOriginOption = countriesOptions.find((option) => option.value === selectedOriginCountryId) || null;
  const selectedDestinationOption = countriesOptions.find((option) => option.value === selectedDestinationCountryId) || null;
  const selectedCentreOption = applicationCentreOptions.find((option) => option.value === selectedApplicationCentreId) || null;
  const selectedCategoryOption = appointmentCategoryOptions.find((option) => option.value === selectedAppointmentCategoryId) || null;
  const selectedSubCategoryOption = subCategoryOptions.find((option) => option.value === selectedSubCategoryId) || null;

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: 44,
      borderRadius: 12,
      borderColor: state.isFocused ? "#f26b2b" : "#d5cfc7",
      boxShadow: state.isFocused ? "0 0 0 4px rgba(242, 107, 43, 0.14)" : "none",
      background: state.isDisabled ? "#f4f1ed" : "#ffffff",
      paddingLeft: 4,
      transition: "all 0.16s ease",
      cursor: state.isDisabled ? "not-allowed" : "text",
      ":hover": {
        borderColor: state.isFocused ? "#f26b2b" : "#c9c0b6",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 10px",
      gap: 8,
    }),
    input: (base) => ({
      ...base,
      color: "#1f1f1f",
      fontSize: 13.5,
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9a948d",
      fontSize: 13.5,
    }),
    singleValue: (base) => ({
      ...base,
      color: "#1f1f1f",
      fontSize: 13.5,
      fontWeight: 600,
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 14,
      overflow: "hidden",
      border: "1px solid #e6e1db",
      boxShadow: "0 16px 36px rgba(40, 32, 28, 0.12)",
      zIndex: 30,
    }),
    menuList: (base) => ({
      ...base,
      padding: 8,
    }),
    option: (base, state) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      gap: 12,
      borderRadius: 10,
      marginBottom: 4,
      padding: "10px 12px",
      backgroundColor: state.isSelected ? "#f26b2b" : state.isFocused ? "#fff1e8" : "#ffffff",
      color: state.isSelected ? "#ffffff" : "#1f1f1f",
      cursor: "pointer",
      fontSize: 13.5,
      fontWeight: state.isSelected ? 700 : 500,
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? "#f26b2b" : "#6b6560",
      paddingRight: 12,
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "#6b6560",
    }),
  };

  const formatCountryOptionLabel = ({ country, label }) => (
    <div className="route-select-option">
      <span className="route-select-flag" aria-hidden="true">{isoCodeToFlag(country.iso_code)}</span>
      <span>{label}</span>
    </div>
  );

  const formatPlainOptionLabel = ({ label }) => <span>{label}</span>;

  useEffect(() => {
    let ignore = false;

    async function loadCountries() {
      setCountriesLoading(true);
      setErrorMessage("");

      try {
        const response = await API.get("/countries");
        if (!ignore) {
          setCountries(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(getErrorMessage(error, "Failed to load countries."));
        }
      } finally {
        if (!ignore) {
          setCountriesLoading(false);
        }
      }
    }

    loadCountries();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    setApplicationCentres([]);
    setAppointmentCategories([]);
    setSubCategories([]);
    setEmptyMessage("");

    if (!selectedOriginCountryId || !selectedDestinationCountryId) {
      return;
    }

    let ignore = false;

    async function loadApplicationCentres() {
      setCentresLoading(true);
      setErrorMessage("");

      try {
        const response = await API.get("/application-centres", {
          params: {
            origin_country_id: selectedOriginCountryId,
            destination_country_id: selectedDestinationCountryId,
          },
        });

        if (!ignore) {
          const centres = Array.isArray(response.data) ? response.data : [];
          setApplicationCentres(centres);
          setEmptyMessage(centres.length === 0 ? "No centre found for this route." : "");
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(getErrorMessage(error, "Failed to load application centres."));
        }
      } finally {
        if (!ignore) {
          setCentresLoading(false);
        }
      }
    }

    loadApplicationCentres();

    return () => {
      ignore = true;
    };
  }, [selectedOriginCountryId, selectedDestinationCountryId]);

  useEffect(() => {
    setAppointmentCategories([]);
    setSubCategories([]);

    if (!selectedOriginCountryId || !selectedDestinationCountryId || !selectedApplicationCentreId) {
      return;
    }

    let ignore = false;

    async function loadAppointmentCategories() {
      setCategoriesLoading(true);
      setErrorMessage("");

      try {
        const response = await API.get("/appointment-categories", {
          params: {
            origin_country_id: selectedOriginCountryId,
            destination_country_id: selectedDestinationCountryId,
            application_centre_id: selectedApplicationCentreId,
          },
        });

        if (!ignore) {
          setAppointmentCategories(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(getErrorMessage(error, "Failed to load appointment categories."));
        }
      } finally {
        if (!ignore) {
          setCategoriesLoading(false);
        }
      }
    }

    loadAppointmentCategories();

    return () => {
      ignore = true;
    };
  }, [selectedOriginCountryId, selectedDestinationCountryId, selectedApplicationCentreId]);

  useEffect(() => {
    setSubCategories([]);

    if (
      !selectedOriginCountryId ||
      !selectedDestinationCountryId ||
      !selectedApplicationCentreId ||
      !selectedAppointmentCategoryId
    ) {
      return;
    }

    let ignore = false;

    async function loadSubCategories() {
      setSubCategoriesLoading(true);
      setErrorMessage("");

      try {
        const response = await API.get("/sub-categories", {
          params: {
            origin_country_id: selectedOriginCountryId,
            destination_country_id: selectedDestinationCountryId,
            application_centre_id: selectedApplicationCentreId,
            appointment_category_id: selectedAppointmentCategoryId,
          },
        });

        if (!ignore) {
          setSubCategories(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(getErrorMessage(error, "Failed to load sub categories."));
        }
      } finally {
        if (!ignore) {
          setSubCategoriesLoading(false);
        }
      }
    }

    loadSubCategories();

    return () => {
      ignore = true;
    };
  }, [
    selectedOriginCountryId,
    selectedDestinationCountryId,
    selectedApplicationCentreId,
    selectedAppointmentCategoryId,
  ]);

  const handleCountryChange = (selectedOption, callback) => {
    if (!selectedOption) {
      callback(null);
      return;
    }

    callback(selectedOption.country || null);
  };

  const handleCentreChange = (selectedOption) => {
    if (!selectedOption) {
      onApplicationCentreChange(null);
      return;
    }

    onApplicationCentreChange(selectedOption.centre || null);
  };

  const handleCategoryChange = (selectedOption) => {
    if (!selectedOption) {
      onAppointmentCategoryChange(null);
      return;
    }

    onAppointmentCategoryChange(selectedOption.category || null);
  };

  const handleSubCategoryChange = (selectedOption) => {
    if (!selectedOption) {
      onSubCategoryChange(null);
      return;
    }

    onSubCategoryChange(selectedOption.subCategory || null);
  };

  return (
    <>
      {errorMessage && <div className="alert alert-danger py-2">{errorMessage}</div>}

      <div className="row g-3 booking-fields mb-3">
        <div className="col-lg-6">
          <label className="form-label fw-semibold">
            Origin / Departure Country <span className="text-danger">*</span>
          </label>
          <Select
            classNamePrefix="booking-select"
            options={countriesOptions}
            value={selectedOriginOption}
            onChange={(option) => handleCountryChange(option, onOriginCountryChange)}
            isDisabled={disabled || countriesLoading}
            isLoading={countriesLoading}
            isClearable
            isSearchable
            placeholder="Search and select origin country"
            styles={selectStyles}
            formatOptionLabel={formatCountryOptionLabel}
            noOptionsMessage={() => "No country found"}
          />
          <div className="route-field-meta">
            {countriesLoading ? "Loading..." : `${countries.length} countries`}
          </div>
        </div>

        <div className="col-lg-6">
          <label className="form-label fw-semibold">
            Destination / Arrival Country <span className="text-danger">*</span>
          </label>
          <Select
            classNamePrefix="booking-select"
            options={countriesOptions}
            value={selectedDestinationOption}
            onChange={(option) => handleCountryChange(option, onDestinationCountryChange)}
            isDisabled={disabled || countriesLoading}
            isLoading={countriesLoading}
            isClearable
            isSearchable
            placeholder="Search and select destination"
            styles={selectStyles}
            formatOptionLabel={formatCountryOptionLabel}
            noOptionsMessage={() => "No country found"}
          />
          <div className="route-field-meta">
            Select route.
          </div>
        </div>
      </div>

      <div className="route-options-panel mb-4">
        <div className="route-options-header">
          <div>
            <h6 className="fw-semibold mb-1">Application Preferences</h6>
          </div>
        </div>

        <div className="row g-3 booking-fields">
          <div className="col-md-6">
            <label className="form-label">Application Centre *</label>
            <Select
              classNamePrefix="booking-select"
              options={applicationCentreOptions}
              value={selectedCentreOption}
              onChange={handleCentreChange}
              isDisabled={
                disabled ||
                !selectedOriginCountryId ||
                !selectedDestinationCountryId ||
                centresLoading
              }
              isLoading={centresLoading}
              isClearable
              isSearchable
              placeholder="Search application centre"
              styles={selectStyles}
              formatOptionLabel={formatPlainOptionLabel}
              noOptionsMessage={() => "No centre found"}
            />
            <div className="route-field-meta">
              {centresLoading
                ? "Loading centres..."
                : selectedOriginCountryId && selectedDestinationCountryId
                  ? `${applicationCentres.length} centre${applicationCentres.length === 1 ? "" : "s"}`
                  : "Select route"}
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Appointment Category *</label>
            <Select
              classNamePrefix="booking-select"
              options={appointmentCategoryOptions}
              value={selectedCategoryOption}
              onChange={handleCategoryChange}
              isDisabled={disabled || !selectedApplicationCentreId || categoriesLoading}
              isLoading={categoriesLoading}
              isClearable
              isSearchable
              placeholder="Search appointment category"
              styles={selectStyles}
              formatOptionLabel={formatPlainOptionLabel}
              noOptionsMessage={() => "No category found"}
            />
            <div className="route-field-meta">
              {categoriesLoading
                ? "Loading categories..."
                : selectedApplicationCentreId
                  ? `${appointmentCategories.length} categor${appointmentCategories.length === 1 ? "y" : "ies"}`
                  : "Choose centre"}
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Sub Category *</label>
            <Select
              classNamePrefix="booking-select"
              options={subCategoryOptions}
              value={selectedSubCategoryOption}
              onChange={handleSubCategoryChange}
              isDisabled={disabled || !selectedAppointmentCategoryId || subCategoriesLoading}
              isLoading={subCategoriesLoading}
              isClearable
              isSearchable
              placeholder="Search sub category"
              styles={selectStyles}
              formatOptionLabel={formatPlainOptionLabel}
              noOptionsMessage={() => "No sub category found"}
            />
            <div className="route-field-meta">
              {subCategoriesLoading
                ? "Loading sub categories..."
                : selectedAppointmentCategoryId
                  ? `${subCategories.length} sub categor${subCategories.length === 1 ? "y" : "ies"}`
                  : "Choose category"}
            </div>
          </div>
        </div>
      </div>

      {emptyMessage && <div className="alert alert-warning py-2 mb-4">{emptyMessage}</div>}
      {!emptyMessage && !categoriesLoading && selectedApplicationCentreId && appointmentCategories.length === 0 && (
        <div className="text-muted mb-4" style={{ fontSize: "13px" }}>
          No appointment category found for this centre.
        </div>
      )}
      {!subCategoriesLoading && selectedAppointmentCategoryId && subCategories.length === 0 && (
        <div className="text-muted mb-4" style={{ fontSize: "13px" }}>
          No sub category found for this appointment category.
        </div>
      )}
    </>
  );
}